import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Send, Loader2 } from 'lucide-react';
import { UpgradeEmailData } from '@/types/subscriptionUpgrade';
import { generateEmailHTML, EmailTemplateData } from '@/utils/emailTemplates';
import { createEmailService } from '@/services/emailService';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface EmailPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailData: UpgradeEmailData;
  isMultipleClients?: boolean;
  onConfirm?: () => void;
  confirmLabel?: string;
  isLoading?: boolean;
}

export const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  isOpen,
  onClose,
  emailData,
  isMultipleClients = false,
  onConfirm,
  confirmLabel,
  isLoading = false
}) => {
  const previewData: EmailTemplateData = isMultipleClients ? {
    ...emailData,
    customerName: '[Customer Name]',
    subscriptionName: '[Your Subscription]',
    currentAmount: 0,
    newAmount: 0,
    gracePeriodEnd: '[30 days from today]',
    changePaymentMethodUrl: emailData.changePaymentMethodUrl,
    nextBillingDate: emailData.nextBillingDate
  } : {
    businessName: emailData.businessName,
    subscriptionName: emailData.subscriptionName,
    customerName: emailData.customerName,
    currentAmount: emailData.currentAmount,
    newAmount: emailData.newAmount,
    gracePeriodEnd: emailData.gracePeriodEnd,
    changePaymentMethodUrl: emailData.changePaymentMethodUrl,
    nextBillingDate: emailData.nextBillingDate
  };

  const emailHtml = generateEmailHTML(previewData);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const { toast } = useToast();

  const handleSendTestEmail = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        toast({
          title: 'Cannot send test email',
          description: 'No email address found for your account.',
          variant: 'destructive',
        });
        return;
      }
      setIsSendingTest(true);
      const emailService = createEmailService();
      await emailService.sendTestEmail(user.email, emailData);
      toast({
        title: 'Test email sent',
        description: `A test email was sent to ${user.email}.`,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send test email.';
      toast({
        title: 'Failed to send test email',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[90vw] overflow-y-auto" hideClose>
        <DialogHeader className="sr-only">
          <DialogTitle>Email Preview</DialogTitle>
        </DialogHeader>

        <div className="space-y-0">
          <div className="border rounded-t-lg bg-white px-4 py-2.5 flex items-center justify-between">
            <Badge variant="secondary" className="text-xs gap-1">
              <Eye className="h-3 w-3" />
              Preview
            </Badge>
            <Button
              variant="outline"
              size="sm"
              className="text-xs gap-1.5"
              onClick={handleSendTestEmail}
              disabled={isSendingTest}
            >
              {isSendingTest ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Send className="h-3 w-3" />
              )}
              {isSendingTest ? 'Sending...' : 'Send me a test email'}
            </Button>
          </div>
          <div className="border border-t-0 rounded-b-lg bg-gray-50" style={{ height: '65vh', minHeight: '450px' }}>
            <iframe
              srcDoc={emailHtml}
              className="w-full h-full border-0 rounded-b-lg"
              title="Email Preview"
              sandbox="allow-same-origin"
            />
          </div>
        </div>

        <DialogFooter className="flex items-center justify-end pt-4 gap-2">
          <div className="flex-shrink-0 flex gap-2">
            <Button variant="outline" onClick={onClose}>
              {onConfirm ? 'Back' : 'Close Preview'}
            </Button>
            {onConfirm && (
              <Button
                onClick={onConfirm}
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isLoading ? 'Sending...' : confirmLabel || 'Confirm'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
